import { Badge } from "@/components/ui/baseUi/badge"
import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { CheckmarkIcon } from "@images/svgIcons/checkmarkIcon"
import { word } from "@modules/core/utils/i18n"
import { getFormatedDateTime } from "@modules/language/languageUtils"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import { useProgramAthleteSubscription } from "@modules/programs/hooks/useProgramSubscription"
import { ProgramSubscriptionStatus, ProgramType } from "@modules/programs/types/programTypes"
import { Clock, CreditCard, LoaderCircle } from "lucide-react"

export function ProgramSubscriptionActions({ programId }: { programId: string }) {
  const { data: program } = useProgram(programId)
  const {
    isUpdating: subscriptionLoading,
    subscribe,
    unsubscribe,
    programStripeSubscriptionInfos,
  } = useProgramAthleteSubscription(programId)

  const shouldDisplayImpossiblePurchase =
    program.owner?.stripeAccountInError &&
    program.price?.value &&
    program.price.value > 0 &&
    (program.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE ||
      program.subscriptionStatus === ProgramSubscriptionStatus.PENDING_PAYMENT)
      ? true
      : false

  switch (program.subscriptionStatus) {
    case ProgramSubscriptionStatus.PENDING_REQUEST:
      return (
        <div className="mt-2 flex w-full flex-row items-center justify-between">
          <FakeSubscriptionButton text={word("program.subscription.pending.label")} />
          <Button
            isLoading={subscriptionLoading}
            variant="underline"
            onClick={() => void unsubscribe()}
            disabled={subscriptionLoading}
          >
            {subscriptionLoading && <LoaderCircle className="animate-spin" />}
            {word("program.subscription.paying.cancel.title")}
          </Button>
        </div>
      )
    case ProgramSubscriptionStatus.PENDING_PAYMENT:
      return (
        <div className="flex flex-col gap-2">
          <div className="flex flex-row items-center gap-2 rounded-2xl border p-4">
            <Badge variant="informationIntense" size="lg">
              <CreditCard color="currentColor" />
              {word("program.subscription.pending.payment.title")}
            </Badge>
            <Text fontStyle="BS" className="text-textDefault-caption">
              {word("program.subscription.pending.payment.information")}
            </Text>
          </div>
          <div className="flex w-full flex-row justify-between">
            <Button
              isLoading={subscriptionLoading}
              variant="primary"
              onClick={() => void subscribe()}
              disabled={subscriptionLoading}
            >
              {subscriptionLoading && <LoaderCircle className="animate-spin" />}
              {word("program.subscription.paying.proceed.title")}
            </Button>
            <Button variant="underline" onClick={() => void unsubscribe()} disabled={subscriptionLoading}>
              {word("program.subscription.paying.cancel.title")}
            </Button>
          </div>
        </div>
      )
    case ProgramSubscriptionStatus.TRIALING:
      return (
        <div className="flex flex-col gap-2">
          {programStripeSubscriptionInfos &&
            "trial" in programStripeSubscriptionInfos &&
            programStripeSubscriptionInfos.trial && (
              <Text>
                {word("program.subscription.trialing.progression", {
                  trialEndDate: getFormatedDateTime(programStripeSubscriptionInfos.trial.endDate),
                })}
              </Text>
            )}
          <Button
            isLoading={subscriptionLoading}
            variant="outline"
            onClick={() => void unsubscribe()}
            disabled={subscriptionLoading}
          >
            {subscriptionLoading && <LoaderCircle className="animate-spin" />}
            {word("program.subscription.trialing.cancel.button")}
          </Button>
        </div>
      )
    case ProgramSubscriptionStatus.ACTIVE:
      if (
        program.type === ProgramType.ONOFF &&
        programStripeSubscriptionInfos &&
        "paidDate" in programStripeSubscriptionInfos &&
        programStripeSubscriptionInfos.paidDate instanceof Date &&
        programStripeSubscriptionInfos.paidPrice?.value
      )
        return (
          <Text className="flex flex-row items-center gap-2 self-start text-textDefault-caption" fontStyle="TS">
            <CheckmarkIcon size={24} color="var(--neutral-700)" />
            {word("program.onOff.paidOn", {
              paymentDate: getFormatedDateTime(programStripeSubscriptionInfos.paidDate),
            })}
          </Text>
        )
      else
        return (
          <Button
            variant="outline"
            onClick={() => void unsubscribe()}
            isLoading={subscriptionLoading}
            disabled={shouldDisplayImpossiblePurchase || subscriptionLoading}
          >
            {subscriptionLoading && <LoaderCircle className="animate-spin" />}
            {program.type === ProgramType.ONOFF
              ? word("program.onOff.subscription.remove.title")
              : word("program.subscription.paying.unsubscribe.confirm.button")}
          </Button>
        )
    case ProgramSubscriptionStatus.PENDING_CANCELLATION: // only for paying subscription
      return <FakeSubscriptionButton text={word("program.subscription.paying.unsubscription_in_progress.title")} />
    case ProgramSubscriptionStatus.INACTIVE:
      return (
        <Button
          variant="primary"
          onClick={() => void subscribe()}
          isLoading={subscriptionLoading}
          disabled={shouldDisplayImpossiblePurchase || subscriptionLoading}
        >
          {subscriptionLoading && <LoaderCircle className="animate-spin" />}
          {program.type === ProgramType.ONOFF
            ? program.price
              ? word("program.onOff.subscription.buy.title")
              : word("program.onOff.subscription.buy.free.title")
            : program.price
              ? word("program.subscription.paying.subscribe.title")
              : word("program.subscription.free.subscribe.title")}
        </Button>
      )
  }
}

export function FakeSubscriptionButton({ text }: { text: string }) {
  return (
    <Button variant="outline" size="sm" disabled className="self-start">
      <Clock color="var(--neutral-700)" size={16} />
      {text}
    </Button>
  )
}
